import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { Col, Row, Label, Input, Table, Button, Spinner, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import Header from "../../../layout/Header/Header";
import jsPDF from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import encode from "nodejs-base64-encode";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { FiltroTipoHabilitacion, coincideTipo } from "../../Materiales/Habilitacion/TiposHabilitacion";

/**
 * Seguimiento de una tela o una habilitacion en el inventario por almacen.
 *
 * Existencia por almacen (con lo apartado y lo disponible) y todos sus
 * movimientos: compras, traspasos a maquileros, consumos de las entradas,
 * salidas, devoluciones y ajustes, con el acumulado. Reemplaza al seguimiento
 * viejo, que leia los rollos del inventario anterior.
 */
function SeguimientoMateriales({ tipo }) {
  const { user } = useContext(AuthContext);
  const esTela = tipo === "Telas";
  const URL_CATALOGO = esTela ? process.env.REACT_APP_URL_TELAS : process.env.REACT_APP_URL_HABILITACION;
  const URL_COLORES = process.env.REACT_APP_URL_COLORES;
  const URL_AREAS = process.env.REACT_APP_URL_AREAS;
  const URL_MOVIMIENTOS = process.env.REACT_APP_URL_MOVIMIENTOS_MATERIALES;
  const URL_SEND_MAIL_ATT = process.env.REACT_APP_URL_SEND_MAIL_ATT;

  const [materiales, setMateriales] = useState([]);
  const [colores, setColores] = useState([]);
  const [areas, setAreas] = useState([]);
  const [material, setMaterial] = useState(null);
  const [filtroTipoHab, setFiltroTipoHab] = useState("");
  const [color, setColor] = useState("");
  const [area, setArea] = useState("");
  const [inicio, setInicio] = useState("");
  const [fin, setFin] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [modalMail, setModalMail] = useState(false);
  const [mailTo, setMailTo] = useState("");

  const cab = () => ({ headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } });

  useEffect(() => {
    axios.get(URL_CATALOGO, cab()).then((r) => setMateriales(r.data.filter((m) => m.is_active !== "No"))).catch((e) => console.log(e));
    axios.get(URL_COLORES, cab()).then((r) => setColores(r.data)).catch((e) => console.log(e));
    axios.get(URL_AREAS, cab()).then((r) => setAreas(r.data)).catch((e) => console.log(e));
  }, [tipo]);

  useEffect(() => {
    if (!material) return setDatos(null);
    // Si el filtro cambia antes de que llegue la respuesta, esa respuesta ya
    // no vale: sin esto una consulta vieja y lenta pisaba a la nueva.
    let vigente = true;
    setCargando(true);
    let url = `${URL_MOVIMIENTOS}/seguimiento?tipoMaterial=${tipo}&material=${material._id}`;
    if (esTela && color) url += `&colores=${color}`;
    if (area) url += `&areas=${area}`;
    if (inicio) url += `&inicio=${inicio}`;
    if (fin) url += `&fin=${fin}`;
    axios.get(url, cab())
      .then((r) => vigente && setDatos(r.data))
      .catch((e) => vigente && Swal.fire({ icon: "error", title: "No se pudo leer el seguimiento", text: e.response?.data?.message || e.message }))
      .finally(() => vigente && setCargando(false));
    return () => { vigente = false; };
  }, [material, color, area, inicio, fin]);

  const n = (x) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(x || 0);
  const nombreMaterial = material ? [material.codigo, material.nombre, esTela ? "" : material.descripcion].filter((x) => x && x !== "NA" && x !== "-").join(" / ") : "";
  const texto = busqueda.toLowerCase();
  const movimientos = (datos?.movimientos || [])
    .filter((m) => !texto || `${m.tipo} ${m.folio} ${m.destino} ${m.almacen} ${m.color} ${m.observaciones}`.toLowerCase().includes(texto))
    .slice()
    .reverse();

  const COLUMNAS = ["Fecha", "Movimiento", "Numero", "Referencia", "Almacen", ...(esTela ? ["Color"] : []), "Entrada", "Salida", "Saldo"];
  const filasExport = () => movimientos.map((m) => [
    m.fecha, m.tipo, m.folio, [m.destino, m.observaciones].filter(Boolean).join(" · "), m.almacen,
    ...(esTela ? [m.color] : []), m.entrada ? n(m.entrada) : "", m.salida ? n(m.salida) : "", n(m.saldo),
  ]);

  function armarPDF() {
    const doc = new jsPDF("landscape");
    const img = new Image();
    img.src = process.env.PUBLIC_URL + "logoInstitucion.png";
    doc.addImage(img, "png", 230, 8, 45, 22);
    doc.text(`Seguimiento ${esTela ? "de Tela" : "de Habilitacion"}: ${nombreMaterial}`, 20, 18);
    doc.setFontSize(10);
    const t = datos?.totales || {};
    doc.text(`Existencia ${n(t.existencia)} · Apartado ${n(t.apartado)} · Disponible ${n(t.disponible)}`, 20, 26);
    doc.autoTable({ head: [COLUMNAS], body: filasExport(), startY: 34, styles: { fontSize: 8 } });
    return doc;
  }
  const PDF = () => armarPDF().save(`Seguimiento-${nombreMaterial}.pdf`);

  function excel() {
    new ExportJsonExcel({
      fileName: `Seguimiento-${nombreMaterial}`,
      datas: [{
        sheetData: filasExport().map((r) => Object.fromEntries(COLUMNAS.map((c, i) => [c, r[i]]))),
        sheetName: "Seguimiento", sheetFilter: COLUMNAS, sheetHeader: COLUMNAS,
      }],
    }).saveExcel();
  }

  function enviaMail() {
    const base64File = encode.encode(armarPDF().output("arraybuffer"), "base64");
    axios.post(URL_SEND_MAIL_ATT, {
      subject: `Seguimiento ${nombreMaterial}`,
      email: mailTo,
      fileName: `Seguimiento-${nombreMaterial}.pdf`,
      att: base64File,
      text: `<table border="0" cellpadding="0" cellspacing="0" width="100%">
         <th><img width="${process.env.REACT_APP_TAMANO_LOGO_MAIL}" align="center" src="${process.env.REACT_APP_LINK_SISTEMA}logoInstitucion.png" /></th>
         <tr align="center" style="font-size:30px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px;font-weight:bold"><br>${process.env.REACT_APP_NOMBRE_INSTITUCION}</tr>
         <tbody>
         <tr style="font-size:20px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px;font-weight:bold"><br>Hola,</tr>
         <tr style="font-size:20px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px;font-weight:bold"><br>Adjunto encontraras el seguimiento de ${nombreMaterial}.</tr>
         </tbody>
         <tr style="font-size:16px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px"><br>Favor de no responder este correo, para cualquier duda favor de comunicarse al ${process.env.REACT_APP_TELEFONO_INSTITUCION}</tr>
         <tr align="right"><br>Powered by <a href="https://lynqro.com"> <img width="20%" align="center" src="${process.env.REACT_APP_LINK_SISTEMA}Logo-Lynqro-PDF.png" /> </a> </tr>
         </table>`,
    }, cab())
      .then(() => {
        setModalMail(false);
        Swal.fire({ icon: "success", title: "Enviado", timer: 1500, showConfirmButton: false });
      })
      .catch((e) => Swal.fire({ icon: "error", title: "No se pudo enviar", text: e.response?.data?.message || e.message }));
  }

  const permiso = esTela ? user.menu_telas : user.menu_habilitacion;

  return (
    <>
      <Header />
      <br />
      <br />
      {permiso ? (
        <div className="card container col-12">
          <div className="page-header">
            <div>
              <h3 className="page-title">
                <i className="fas fa-search" style={{ marginRight: 8, color: "#0ea5e9" }} />
                Seguimiento {esTela ? "de Telas" : "de Habilitacion"}
              </h3>
              <p className="page-subtitle">Existencia por almacen y todos los movimientos, con su acumulado</p>
            </div>
            {datos ? (
              <div>
                <Button color="danger" size="sm" onClick={PDF} style={{ marginRight: 6 }}>PDF</Button>
                <Button color="info" size="sm" onClick={() => setModalMail(true)} style={{ marginRight: 6 }}>eMail</Button>
                <Button color="success" size="sm" onClick={excel}>Excel</Button>
              </div>
            ) : undefined}
          </div>

          <Row style={{ marginBottom: 12 }}>
            {esTela ? undefined : (
              <Col md={2}>
                <Label>Tipo</Label>
                <FiltroTipoHabilitacion value={filtroTipoHab} onChange={setFiltroTipoHab} />
              </Col>
            )}
            <Col md={4}>
              <Label>{esTela ? "Tela" : "Habilitacion"}</Label>
              <Autocomplete
                size="small"
                options={materiales
                  .filter((m) => esTela || coincideTipo(m, filtroTipoHab))
                  .sort((a, b) => ((a.nombre || "") > (b.nombre || "") ? 1 : -1))}
                value={material}
                onChange={(e, v) => { setMaterial(v); setColor(""); }}
                getOptionLabel={(m) => [m.codigo, m.nombre, esTela ? "" : m.descripcion].filter((x) => x && x !== "NA" && x !== "-").join(" / ")}
                renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Busca por nombre o codigo" />}
              />
            </Col>
            {esTela ? (
              <Col md={2}>
                <Label>Color</Label>
                <Input type="select" value={color} onChange={(e) => setColor(e.target.value)}>
                  <option value="">Todos</option>
                  {colores.slice().sort((a, b) => (a.name > b.name ? 1 : -1)).map((c) => (
                    <option value={c._id} key={c._id}>{c.name}</option>
                  ))}
                </Input>
              </Col>
            ) : undefined}
            <Col md={2}>
              <Label>Almacen</Label>
              <Input type="select" value={area} onChange={(e) => setArea(e.target.value)}>
                <option value="">Todos</option>
                {areas.slice().sort((a, b) => (a.name > b.name ? 1 : -1)).map((a) => (
                  <option value={a._id} key={a._id}>{a.name}</option>
                ))}
              </Input>
            </Col>
            <Col md={2}>
              <Label>Desde</Label>
              <Input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} />
            </Col>
            <Col md={2}>
              <Label>Hasta</Label>
              <Input type="date" value={fin} onChange={(e) => setFin(e.target.value)} />
            </Col>
          </Row>

          {!material ? (
            <p className="text-muted">Elige {esTela ? "una tela" : "una habilitacion"} para ver su seguimiento.</p>
          ) : cargando ? (
            <Spinner />
          ) : datos ? (
            <>
              <h5>Existencia</h5>
              <Table size="sm" borderless style={{ maxWidth: 820 }}>
                <thead>
                  <tr>
                    <th>Almacen</th>
                    {esTela ? <th>Color</th> : undefined}
                    <th style={{ textAlign: "right" }}>Existencia</th>
                    <th style={{ textAlign: "right" }}>Apartado</th>
                    <th style={{ textAlign: "right" }}>Disponible</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.saldos.map((s) => (
                    <tr key={s.almacen + s.color}>
                      <td>{s.almacen}{s.tipoAlmacen === "Maquilero" ? " (maquilero)" : ""}</td>
                      {esTela ? <td>{s.color}</td> : undefined}
                      <td style={{ textAlign: "right" }}>{n(s.existencia)}</td>
                      <td style={{ textAlign: "right", color: s.apartado > 0 ? "#8b5cf6" : undefined }}>{n(s.apartado)}</td>
                      <td style={{ textAlign: "right", fontWeight: 600 }}>{n(s.disponible)}</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: "1px solid #e2e8f0" }}>
                    <td><b>TOTAL</b></td>
                    {esTela ? <td /> : undefined}
                    <td style={{ textAlign: "right" }}><b>{n(datos.totales.existencia)}</b></td>
                    <td style={{ textAlign: "right" }}><b>{n(datos.totales.apartado)}</b></td>
                    <td style={{ textAlign: "right" }}><b>{n(datos.totales.disponible)}</b></td>
                  </tr>
                </tbody>
              </Table>

              <Row style={{ marginBottom: 8 }}>
                <Col md={4}>
                  <Input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar movimiento, numero, proveedor, maquilero..." />
                </Col>
              </Row>
              <h5>Movimientos (el mas reciente arriba)</h5>
              <Table size="sm" striped borderless responsive>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Movimiento</th>
                    <th>Numero</th>
                    <th>Referencia</th>
                    <th>Almacen</th>
                    {esTela ? <th>Color</th> : undefined}
                    <th style={{ textAlign: "right" }}>Entrada</th>
                    <th style={{ textAlign: "right" }}>Salida</th>
                    <th style={{ textAlign: "right" }}>{area ? "Saldo almacen" : "Saldo total"}</th>
                    <th>Usuario</th>
                  </tr>
                </thead>
                <tbody>
                  {movimientos.map((m) => (
                    <tr key={m._id}>
                      <td>{m.fecha}</td>
                      <td>{m.tipo}</td>
                      <td>{m.folio}</td>
                      <td>{[m.destino, m.observaciones].filter(Boolean).join(" · ")}</td>
                      <td>{m.almacen}</td>
                      {esTela ? <td>{m.color}</td> : undefined}
                      <td style={{ textAlign: "right", color: "#16a34a" }}>{m.entrada ? n(m.entrada) : ""}</td>
                      <td style={{ textAlign: "right", color: "#dc2626" }}>{m.salida ? n(m.salida) : ""}</td>
                      <td style={{ textAlign: "right", fontWeight: 600 }}>{n(m.saldo)}</td>
                      <td>{m.usuario}</td>
                    </tr>
                  ))}
                  {!movimientos.length ? (
                    <tr><td colSpan={10} className="text-muted">Sin movimientos en ese periodo.</td></tr>
                  ) : undefined}
                </tbody>
              </Table>
            </>
          ) : undefined}

          <Modal size="sm" isOpen={modalMail} toggle={() => setModalMail(false)}>
            <ModalHeader toggle={() => setModalMail(false)}><h4>Enviar por correo</h4></ModalHeader>
            <ModalBody>
              <Label>Correo</Label>
              <Input type="email" value={mailTo} onChange={(e) => setMailTo(e.target.value)} />
            </ModalBody>
            <ModalFooter>
              <Button color="success" onClick={enviaMail} disabled={!mailTo}>Enviar</Button>
            </ModalFooter>
          </Modal>
        </div>
      ) : undefined}
    </>
  );
}

export default SeguimientoMateriales;
